/**
 * Allowed values for recruiting campaign profile intake.
 * Form stores `value` (backend / SSOT brand code); UI shows `label`.
 */

export const INTAKE_BRAND_OPTIONS = [
  {
    value: "PROPERTIES",
    label: "@properties Christie's International Real Estate",
  },
  { value: "ALLENTATE", label: "Allen Tate Realtors" },
  {
    value: "BERKSHIRE",
    label: "Berkshire Hathaway HomeServices",
  },
  {
    value: "BETTERHOMES",
    label: "Better Homes and Gardens Real Estate",
  },
  { value: "CENTURY21", label: "Century 21" },
  { value: "COLDWELL", label: "Coldwell Banker" },
  { value: "COMPASS", label: "Compass" },
  { value: "CRYELIKE", label: "Crye-Leike" },
  { value: "DOUGLASELLIMAN", label: "Douglas Elliman" },
  { value: "EDINA", label: "Edina Realty" },
  { value: "EXP", label: "eXp Realty" },
  { value: "EXIT", label: "EXIT Realty" },
  { value: "EPIQUE", label: "Epique Realty" },
  { value: "ERA", label: "ERA Real Estate" },
  { value: "FATHOM", label: "Fathom Realty" },
  { value: "HOMESMART", label: "HomeSmart" },
  {
    value: "HOWARDHANNA",
    label: "Howard Hanna Real Estate Services",
  },
  { value: "HUNTREALESTATE", label: "Hunt Real Estate" },
  { value: "INDEPENDENT", label: "Independent" },
  { value: "KELLERWILLIAMS", label: "Keller Williams Realty" },
  { value: "LONGBANDFOSTER", label: "Long & Foster Real Estate" },
  { value: "LPTREALTY", label: "LPT Realty" },
  { value: "NEXTHOME", label: "NextHome" },
  { value: "REMAX", label: "RE/MAX" },
  { value: "THEREALBROKERAGE", label: "The Real Brokerage" },
  { value: "REALTYEXECUTIVES", label: "Realty Executives" },
  { value: "REALTYONE", label: "Realty ONE Group" },
  { value: "REDFIN", label: "Redfin" },
  { value: "RIGHTATHOME", label: "Right at Home Realty" },
  { value: "ROYALLEPAGE", label: "Royal LePage" },
  { value: "SERHANT", label: "SERHANT." },
  { value: "SIDE", label: "Side" },
  { value: "SOTHEBYS", label: "Sotheby's International Realty" },
  { value: "SUTTON", label: "Sutton Group" },
  { value: "UNITEDREALESTATE", label: "United Real Estate" },
  { value: "THEAGENCY", label: "The Agency" },
  { value: "WEICHERT", label: "Weichert, Realtors" },
  { value: "WINDERMERE", label: "Windermere Real Estate" },
]

/** US states + Canadian provinces/territories (Airtable single-select). */
export const STATE_OPTIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY",
  "AB",
  "BC",
  "MB",
  "NB",
  "NL",
  "NS",
  "NVT",
  "NWT",
  "ON",
  "PEI",
  "QC",
  "SK",
  "YT",
].map((code) => ({ value: code, label: code }))

/** Must match Airtable «Time Zone» options on Onboarding Intake Forms. */
export const TIMEZONE_OPTIONS = [
  { value: "+1.5 (Newfoundland)", label: "+1.5 (Newfoundland)" },
  { value: "+1 (Atlantic)", label: "+1 (Atlantic)" },
  { value: "- 0 (Eastern)", label: "- 0 (Eastern)" },
  { value: "-1 (Central)", label: "-1 (Central)" },
  { value: "-2 (Mountain)", label: "-2 (Mountain)" },
  { value: "-3 (Pacific)", label: "-3 (Pacific)" },
  { value: "-4 (Alaska)", label: "-4 (Alaska)" },
  { value: "-5 (Hawaii)", label: "-5 (Hawaii)" },
]

const LEGACY_TIMEZONE_TO_OPTION = {
  "america/new_york": "- 0 (Eastern)",
  "america/chicago": "-1 (Central)",
  "america/denver": "-2 (Mountain)",
  "america/los_angeles": "-3 (Pacific)",
  "america/anchorage": "-4 (Alaska)",
  "pacific/honolulu": "-5 (Hawaii)",
  "america/halifax": "+1 (Atlantic)",
  "america/st_johns": "+1.5 (Newfoundland)",
  "eastern est": "- 0 (Eastern)",
  "central cst": "-1 (Central)",
  "mountain mst": "-2 (Mountain)",
  "pacific pst": "-3 (Pacific)",
  "alaska akst": "-4 (Alaska)",
  "hawaii hst": "-5 (Hawaii)",
  "atlantic ast": "+1 (Atlantic)",
  "newfoundland nst": "+1.5 (Newfoundland)",
}

const BRAND_LOOKUP = (() => {
  const byValue = new Map()
  const byLabel = new Map()
  for (const option of INTAKE_BRAND_OPTIONS) {
    byValue.set(option.value.toUpperCase(), option.value)
    byLabel.set(option.label.toLowerCase(), option.value)
    byLabel.set(option.value.toLowerCase(), option.value)
  }
  return { byValue, byLabel }
})()

const STATE_LOOKUP = new Set(STATE_OPTIONS.map((o) => o.value))

export function normalizeBrandValue(value) {
  const s = String(value ?? "").trim()
  if (!s) return ""
  const upper = s.toUpperCase()
  if (BRAND_LOOKUP.byValue.has(upper)) {
    return BRAND_LOOKUP.byValue.get(upper)
  }
  const byLabel = BRAND_LOOKUP.byLabel.get(s.toLowerCase())
  return byLabel || ""
}

export function brandOptionLabel(value) {
  const code = normalizeBrandValue(value)
  if (!code) return "—"
  const option = INTAKE_BRAND_OPTIONS.find((o) => o.value === code)
  return option ? option.label : code
}

export function normalizeStateValue(value) {
  const s = String(value ?? "")
    .trim()
    .toUpperCase()
  if (!s) return ""
  return STATE_LOOKUP.has(s) ? s : ""
}

export function stateOptionLabel(value) {
  const code = normalizeStateValue(value)
  return code || "—"
}

export function normalizeTimezoneValue(value) {
  const s = String(value ?? "").trim()
  if (!s) return ""
  const direct = TIMEZONE_OPTIONS.find(
    (o) =>
      o.value === s ||
      o.value.toLowerCase() === s.toLowerCase() ||
      o.label.toLowerCase() === s.toLowerCase()
  )
  if (direct) return direct.value
  const legacy = LEGACY_TIMEZONE_TO_OPTION[s.toLowerCase()]
  if (legacy) return legacy
  return ""
}

export function timezoneOptionLabel(value) {
  const v = normalizeTimezoneValue(value)
  if (!v) return "—"
  const option = TIMEZONE_OPTIONS.find((o) => o.value === v)
  return option ? option.label : v
}
